import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dealTypesApi } from '@/lib/api';
import { toast } from '@solid/ui';
import type { DealType, QualifyingQuestion } from '@/types';

const DEAL_TYPES_KEY = ['dealTypes'];

export function useDealTypes() {
  return useQuery<DealType[]>({
    queryKey: DEAL_TYPES_KEY,
    queryFn: dealTypesApi.list,
  });
}

export function useDealType(id: string) {
  return useQuery<DealType>({
    queryKey: [...DEAL_TYPES_KEY, id],
    queryFn: () => dealTypesApi.getById(id),
    enabled: !!id,
  });
}

export function useCreateDealType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: dealTypesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DEAL_TYPES_KEY });
      toast.success('Project type created');
    },
    onError: (error: any) => {
      toast.error(`Failed to create project type: ${error.response?.data?.message || error.message}`);
    },
  });
}

export function useUpdateDealType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof dealTypesApi.update>[1] }) =>
      dealTypesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DEAL_TYPES_KEY });
      toast.success('Project type updated');
    },
    onError: (error: any) => {
      toast.error(`Failed to update project type: ${error.response?.data?.message || error.message}`);
    },
  });
}

export function useDeleteDealType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: dealTypesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DEAL_TYPES_KEY });
      toast.success('Project type deleted');
    },
    onError: (error: any) => {
      toast.error(`Failed to delete project type: ${error.response?.data?.message || error.message}`);
    },
  });
}

export function useReorderDealTypes() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: dealTypesApi.reorder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DEAL_TYPES_KEY });
    },
    onError: (error: any) => {
      toast.error(`Failed to reorder project types: ${error.response?.data?.message || error.message}`);
    },
  });
}

// Question association hooks
export function useDealTypeQuestions(id: string) {
  return useQuery<QualifyingQuestion[]>({
    queryKey: [...DEAL_TYPES_KEY, id, 'questions'],
    queryFn: () => dealTypesApi.getQuestions(id),
    enabled: !!id,
  });
}

export function useSetDealTypeQuestions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, questionIds }: { id: string; questionIds: string[] }) =>
      dealTypesApi.setQuestions(id, questionIds),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [...DEAL_TYPES_KEY, id, 'questions'] });
      toast.success('Questions updated');
    },
    onError: (error: any) => {
      toast.error(`Failed to update questions: ${error.response?.data?.message || error.message}`);
    },
  });
}
